import { Star } from "lucide-react";
import { Reveal } from "./Reveal";

const Stars = ({ size = 10 }) => (
  <span aria-hidden="true" className="inline-flex items-center gap-0.5 text-clay">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
    ))}
  </span>
);

const IDEAS = [
  {
    n: "01",
    title: ["You stop solving", "last night\u2019s problem", "every morning."],
    body: "One rough night doesn\u2019t necessarily mean the whole plan is wrong. Tell me what happened first. Then we can decide whether anything actually needs to change.",
  },
  {
    n: "02",
    title: ["2:13 a.m. is a terrible time", "to make a new sleep plan."],
    body: "With a plan, this moment is different. You\u2019re not deciding all over again at 2:13 a.m. — feed, wait, go in, start over. You already know what tonight gets, and why.",
    after: "The plan should answer those questions before you\u2019re standing beside the crib.",
  },
  {
    n: "03",
    title: ["One 32-minute nap", "stops rewriting", "the entire day."],
    body: "A short nap is information. One difficult bedtime is information. A night waking is information. The question is what the pattern is telling us—not how quickly we can change everything because yesterday was hard.",
  },
];

const CLOSING_MAJOR = ["You don\u2019t need to get", "better at thinking", "about sleep."];
const CLOSING_PAYOFF = ["The goal is understanding", "your child\u2019s sleep", "well enough to think", "about it less."];

export const ValueMoment = () => (
  <section id="what-changes" data-testid="value-moment-section" className="bg-creamsoft">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 py-24 lg:py-36">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
                When sleep starts making sense
              </p>
            </Reveal>
            <Reveal as="h2" delay={0.08} className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl leading-[1.08] tracking-tight">
              One bad night
              <span className="block">stops having the power</span>
              <span className="block italic text-clay">to change everything.</span>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-8 max-w-md text-base sm:text-lg font-normal leading-relaxed text-smoke">
                Once you know what you&rsquo;re actually working on, every short
                nap, false start or night waking doesn&rsquo;t automatically
                require a brand-new plan.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-6 lg:col-start-7 lg:pt-24">
          {IDEAS.map((idea, i) => (
            <Reveal key={idea.n} delay={0.1 + i * 0.12} className="border-t border-ink/10">
              <div data-testid={`value-idea-${i}`} className="py-8 lg:py-12 grid grid-cols-[auto_1fr] gap-6 sm:gap-10 items-baseline">
                <span aria-hidden="true" className="font-serif font-light text-clay/60 text-sm sm:text-base">
                  {idea.n}
                </span>
                <div>
                  <h3 className="font-serif font-light text-ink text-2xl sm:text-3xl lg:text-4xl leading-[1.18]">
                    {idea.title.map((line) => (
                      <span key={line} className="block">{line}</span>
                    ))}
                  </h3>
                  <p className="mt-4 text-base sm:text-lg font-normal leading-relaxed text-smoke max-w-lg">
                    {idea.body}
                  </p>
                  {idea.after && (
                    <p className="mt-5 font-serif italic font-light text-xl sm:text-2xl text-clay leading-snug max-w-lg">
                      {idea.after}
                    </p>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={0.1} className="mt-24 lg:mt-36">
        <div data-testid="value-closing">
          <p className="font-serif font-light text-ink text-3xl sm:text-4xl lg:text-[3.4rem] leading-[1.14] tracking-tight max-w-4xl">
            {CLOSING_MAJOR.map((line) => (
              <span key={line} className="block">{line}</span>
            ))}
          </p>
          <p className="mt-8 lg:mt-10 font-serif italic font-light text-3xl sm:text-4xl lg:text-[3.4rem] leading-[1.14] tracking-tight text-clay max-w-4xl">
            {CLOSING_PAYOFF.map((line) => (
              <span key={line} className="block">{line}</span>
            ))}
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.1} className="mt-24 lg:mt-32 mb-4 lg:mb-8">
        <blockquote data-testid="review-rylee" className="max-w-2xl">
          <div aria-hidden="true" className="h-px w-12 bg-clay/60 mb-10" />
          <p className="font-serif italic font-light text-2xl sm:text-3xl leading-snug text-ink">
            Andrea is amazing! She&rsquo;s incredibly responsive, kind, and
            guides you through the process in a way that holds you accountable —
            but always meets you where you are as a parent and respects what
            you&rsquo;re comfortable with.
          </p>
          <footer className="mt-7 flex items-center gap-3 text-[11px] tracking-[0.18em] uppercase font-semibold text-smoke">
            — Rylee Wells
            <Stars />
            Google review
          </footer>
        </blockquote>
      </Reveal>
    </div>
  </section>
);
